import React, { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { HardDrive, Database, Trash2, RefreshCw, AlertTriangle, Loader2, Sparkles, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMaintenance() {
  const { getAdminAxios } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeDays, setPurgeDays] = useState(30);
  const [confirmAction, setConfirmAction] = useState(null); // {type, label, fn}
  const [actionRunning, setActionRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const fetchStats = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await getAdminAxios().get(`${API}/admin/maintenance/storage`);
      setStats(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); setRefreshing(false); }
  }, [getAdminAxios]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const runConfirmed = async () => {
    if (!confirmAction) return;
    setActionRunning(true);
    try {
      const data = await confirmAction.fn();
      setLastResult({ ok: true, message: data?.message || "Done", detail: data });
      await fetchStats();
    } catch (err) {
      setLastResult({ ok: false, message: err.response?.data?.detail || err.message, detail: null });
    } finally {
      setActionRunning(false);
      setConfirmAction(null);
    }
  };

  const purgeOldMessages = () =>
    getAdminAxios().post(`${API}/admin/maintenance/purge-old-messages?days=${purgeDays}`).then(r => r.data);

  const clearAllWaCache = () =>
    getAdminAxios().post(`${API}/admin/maintenance/clear-all-whatsapp-cache`).then(r => r.data);

  if (loading) {
    return <div className="p-8"><div className="h-96 bg-white/5 rounded-xl animate-pulse" /></div>;
  }

  const disk = stats?.disk || {};
  const usedPct = disk.used_pct || 0;
  const usedColor = usedPct >= 90 ? "bg-red-500" : usedPct >= 75 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="p-8" data-testid="admin-maintenance-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <HardDrive className="w-6 h-6 text-amber-400" />
            Mantenimiento & Almacenamiento
          </h1>
          <p className="text-sm text-slate-400 mt-1">Monitor disk usage and free space across the platform</p>
        </div>
        <Button onClick={fetchStats} disabled={refreshing} variant="outline"
                className="border-white/10 text-slate-200 hover:bg-white/10 bg-transparent" data-testid="refresh-stats">
          {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refrescar
        </Button>
      </div>

      {lastResult && (
        <div className={`mb-6 p-4 rounded-lg border ${lastResult.ok ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-red-500/10 border-red-500/30 text-red-200"}`}
             data-testid="action-result">
          <p className="text-sm font-medium">{lastResult.message}</p>
          {lastResult.detail && lastResult.detail.freed_human && (
            <p className="text-xs opacity-80 mt-1">Liberados: {lastResult.detail.freed_human}</p>
          )}
        </div>
      )}

      {/* Disk Card */}
      <Card className="bg-[#13112b] border-indigo-900/30 mb-6">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-indigo-300" /> Disco /app
          </CardTitle>
          <CardDescription className="text-slate-400">Espacio en el contenedor</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-slate-300">{disk.used_human || "—"} / {disk.total_human || "—"}</span>
            <span className={`font-semibold ${usedPct >= 90 ? "text-red-400" : usedPct >= 75 ? "text-amber-400" : "text-emerald-400"}`} data-testid="disk-pct">
              {usedPct}%
            </span>
          </div>
          <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
            <div className={`h-full ${usedColor} transition-all`} style={{ width: `${Math.min(usedPct, 100)}%` }} />
          </div>
          <p className="text-xs text-slate-500">Libre: {disk.free_human || "—"}</p>
        </CardContent>
      </Card>

      {/* Database & WA Cache */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="bg-[#13112b] border-indigo-900/30">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Database className="w-4 h-4 text-indigo-300" /> MongoDB
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-slate-300">
            <div className="flex justify-between"><span>Datos:</span><span className="font-mono" data-testid="db-data-size">{stats?.database?.data_size_human || "—"}</span></div>
            <div className="flex justify-between"><span>Storage:</span><span className="font-mono">{stats?.database?.storage_size_human || "—"}</span></div>
            <div className="flex justify-between"><span>Documentos:</span><span className="font-mono">{stats?.database?.objects?.toLocaleString() || 0}</span></div>
            <div className="pt-2 border-t border-white/5 grid grid-cols-2 gap-1 text-xs text-slate-400">
              {Object.entries(stats?.collection_counts || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2"><span className="truncate">{k}</span><span className="font-mono">{v}</span></div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#13112b] border-indigo-900/30">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" /> Caché WhatsApp
            </CardTitle>
            <CardDescription className="text-slate-400 text-xs">Sesiones Baileys persistidas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300">Tamaño total</span>
              <span className="font-mono text-amber-400 text-lg" data-testid="wa-cache-size">{stats?.whatsapp_cache_human || "0 B"}</span>
            </div>
            <p className="text-xs text-slate-500 break-all">Path: {stats?.whatsapp_cache_path}</p>
            <Button
              onClick={() => setConfirmAction({
                type: "wipe-wa",
                label: "Borrar TODO el caché de WhatsApp",
                description: "Todos los clientes deberán volver a escanear el QR. Libera espacio en disco. ¿Continuar?",
                fn: clearAllWaCache,
                danger: true,
              })}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium"
              data-testid="clear-wa-cache-all"
            >
              <Zap className="w-4 h-4 mr-2" /> Borrar caché completo
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Purge old messages */}
      <Card className="bg-[#13112b] border-indigo-900/30">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-red-400" /> Purgar mensajes antiguos
          </CardTitle>
          <CardDescription className="text-slate-400 text-xs">Elimina mensajes de Inbox más viejos que N días. Las conversaciones permanecen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-3">
            <div className="flex-1 max-w-xs">
              <Label className="text-slate-300 text-xs">Días de antigüedad</Label>
              <Input type="number" min={1} max={3650} value={purgeDays}
                     onChange={e => setPurgeDays(Math.max(1, parseInt(e.target.value) || 30))}
                     className="bg-white/5 border-white/10 text-white"
                     data-testid="purge-days-input" />
            </div>
            <Button
              onClick={() => setConfirmAction({
                type: "purge",
                label: `Purgar mensajes >${purgeDays} días`,
                description: `Se eliminarán todos los mensajes con timestamp anterior a ${purgeDays} días. Esto NO se puede deshacer.`,
                fn: purgeOldMessages,
                danger: true,
              })}
              disabled={purging}
              className="bg-red-500/80 hover:bg-red-600 text-white"
              data-testid="purge-messages-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Purgar
            </Button>
          </div>
          <p className="text-xs text-amber-400/80 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Esta acción es irreversible.
          </p>
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <DialogContent className="bg-[#13112b] border-indigo-900/30 text-white" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              {confirmAction?.label || "Confirmar acción"}
            </DialogTitle>
            <DialogDescription className="text-slate-400 pt-2">
              {confirmAction?.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5"
                    onClick={() => setConfirmAction(null)} disabled={actionRunning}>Cancelar</Button>
            <Button onClick={runConfirmed} disabled={actionRunning}
                    className={confirmAction?.danger ? "bg-red-500 hover:bg-red-600 text-white" : "bg-amber-500 hover:bg-amber-600 text-black"}
                    data-testid="confirm-action-btn">
              {actionRunning && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
